import React from 'react';

import { css } from '@emotion/css';
import { Theme, Typography } from '@mui/material';
import { Link, useMatchRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { FontSizes } from '@/assets/fonts';
import { Icons } from '@/assets/icons';
import { MenuActionTooltip } from '@/components/floating/MenuTooltip';
import { usePatientIdFromURL } from '@/hooks/usePatientIdFromURL';
import { useStyles } from '@/hooks/useStyles';
import { useWindowSize } from '@/hooks/useWindowSize';
import { useChatMessagingState } from '@/uiKit/organisms/ChatMessaging/ChatMessagingState.ts';
import { isProd } from '@/utils/commonUtils.ts';

const MenuItems = [
  {
    id: 'manage-patient-menu-monitoring',
    path: '/patients/$id',
    title: 'patientNavigation.monitoring',
    link: (id: string | undefined) => `/patients/${id}`,
  },
  {
    id: 'manage-patient-menu-settings',
    path: '/patients/$id/settings',
    title: 'patientNavigation.settings',
    link: (id: string | undefined) => `/patients/${id}/settings`,
  },
  {
    id: 'manage-patient-menu-medical-file',
    path: '/patients/$id/medical-file',
    title: 'patientNavigation.medicalFile',
    link: (id: string | undefined) => `/patients/${id}/medical-file`,
  },
  ...(!isProd()
    ? [
        {
          id: 'manage-patient-menu-patient-management',
          path: '/patients/$id/patient-management',
          title: 'patientNavigation.dataManagement',
          link: (id: string | undefined) =>
            `/patients/${id}/patient-management`,
        },
      ]
    : []),
];

export const PatientMenu: React.FC = () => {
  const chatIsOpen = useChatMessagingState(state => state.isOpen);
  const { screenSize } = useWindowSize();

  switch (true) {
    case screenSize === 'SM' ||
      (['MD', 'LG', 'XL'].includes(screenSize) && chatIsOpen):
      return <PatientMenuSmall />;
    default:
      return <PatientMenuFull />;
  }
};

const PatientMenuSmall: React.FC = () => {
  const styles = useStyles(makeStyles);
  const id = usePatientIdFromURL();
  const { t } = useTranslation();
  const routeMatches = useMatchRoute();
  const selectedItem = MenuItems.find(item => routeMatches({ to: item.path }));

  return (
    <div className={styles.menuSmall}>
      {selectedItem ? (
        <MenuItem
          id={selectedItem.id}
          selected={true}
          link={selectedItem.link(id)}
          title={t(selectedItem.title)}
        />
      ) : null}
      <MenuActionTooltip
        className={styles.tooltipAction}
        wrapperClassName={styles.tooltipWrapper}
        placement="bottom-start"
        actions={MenuItems.map(item =>
          item.title !== selectedItem?.title ? (
            <SmallMenuItem
              id={item.id}
              key={item.title}
              title={t(item.title)}
              link={item.link(id)}
            />
          ) : null,
        )}
      >
        <Icons.hamburger className={styles.icon} />
      </MenuActionTooltip>
    </div>
  );
};

const PatientMenuFull: React.FC = () => {
  const styles = useStyles(makeStyles);
  const id = usePatientIdFromURL();
  const { t } = useTranslation();
  const routeMatches = useMatchRoute();
  return (
    <div className={styles.container}>
      {MenuItems.map(item => (
        <MenuItem
          id={item.id}
          key={item.title}
          selected={routeMatches({ to: item.path })}
          title={t(item.title)}
          link={item.link(id)}
        />
      ))}
    </div>
  );
};

type MenuItemProps = {
  id: string;
  title: string;
  link: string;
  selected: boolean;
};
const MenuItem: React.FC<MenuItemProps> = ({ id, title, link, selected }) => {
  const styles = useStyles(makeMenuItemStyles, selected);
  const navigate = useNavigate();

  return (
    <div
      id={id}
      className={styles.menuItem}
      onClick={() => navigate({ to: link })}
    >
      <div className={styles.menuItemLink}>
        <Typography variant="subtitle" className={styles.text}>
          {title}
        </Typography>
      </div>
    </div>
  );
};

type SmallMenuItemProps = {
  id: string;
  title: string;
  link: string;
};

const SmallMenuItem: React.FC<SmallMenuItemProps> = ({ id, title, link }) => {
  const styles = useStyles(makeMenuItemStyles, false);

  return (
    <Link id={id} to={link}>
      <Typography variant="body" className={styles.text}>
        {title}
      </Typography>
    </Link>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: row;
  `,
  tooltipAction: css({
    display: 'flex',
    alignSelf: 'stretch',
    alignItems: 'center',
  }),
  tooltipWrapper: css({
    display: 'flex',
  }),
  menuSmall: css`
    display: flex;
    flex-direction: row;
    align-items: center;
  `,
  icon: css`
    margin-left: ${theme.spacing(8)};
    width: ${FontSizes.body};
    height: ${FontSizes.body};
    color: ${theme.palette.text.primary};
  `,
});

const makeMenuItemStyles = (theme: Theme, selected: boolean) => ({
  menuItem: css`
    display: flex;
    flex-direction: column;
    cursor: pointer;
    border-radius: ${theme.spacing(2)} ${theme.spacing(2)} 0 0;

    &:hover {
      background-color: ${theme.palette.background.default};
      a {
        border-bottom: ${!selected
          ? `${theme.palette.background.paper} ${theme.spacing(1)} solid`
          : ''};
      }
    }
  `,
  menuItemLink: css`
    flex-grow: 1;
    margin: ${theme.spacing(3)} ${theme.spacing(8)} 0;
    &::after {
      content: '';
      position: relative;
      display: ${selected ? 'block' : 'none'};
      left: 0;
      right: 0;
      bottom: 0;
      height: ${theme.spacing(1)};
      width: 100%;
      background: ${theme.palette.primary.main};
    }
  `,
  text: css`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: clip;
    font-weight: 600;
    color: ${selected ? theme.palette.primary.main : ''};
  `,
});
